/**
 * Billing — current tier, partner credit packs, usage meters and billing
 * history (with invoice download). There are no subscriptions (D-05a) and no
 * saved cards: payment details are only ever entered in Razorpay Checkout.
 *
 * The "Plans & pricing" tier cards (Beta/Pro/Premium with a monthly/yearly
 * toggle) are deliberately hidden until real pricing exists for the
 * product — InviteCloud has no live billing backend yet, so showing
 * placeholder prices would misrepresent what a user could actually buy.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { cn } from '@/src/infrastructure/lib/utils';
import { SettingsCard, Pill } from '@/src/modules/components/settings/primitives';
import { useToast } from '@/src/modules/components/settings/ToastProvider';
import { PartnerCreditPacksSection } from '@/src/modules/partner/components/PartnerCreditPacksSection';
import { createCheckout, downloadInvoice, listPurchases, retryCheckout } from '@/src/modules/purchase/api';
import { openRazorpayCheckout, CheckoutUnavailableError } from '@/src/modules/purchase/razorpayCheckout';
import { relayCheckoutResult } from '@/src/modules/purchase/relayCheckoutResult';
import { pollPurchaseUntilResolved, PAYMENT_UNAVAILABLE_MESSAGE } from '@/src/modules/purchase/pollPurchaseOutcome';
import type { PurchaseListItem } from '@/src/modules/purchase/types';
import { formatPrice } from '@/src/infrastructure/lib/currency';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';
import { getInvitationStats, listInvitations } from '@/src/infrastructure/lib/api';
import { usePublishEntitlement } from '@/src/modules/entitlements/usePublishEntitlement';

/** Key the removed payment-method card used (`usePersistentState` prefix + 'paymentMethod'). */
export const LEGACY_PAYMENT_METHOD_KEY = 'invitecloud.settings.paymentMethod';

type PlanId = 'free' | 'pro' | 'premium';

const PLAN_LABELS: Record<PlanId, string> = {
  free: 'Starter',
  pro: 'Pro',
  premium: 'Premium',
};

/** Maps the backend plan tier (FREE/BASIC/PRO/STUDIO) to a display plan. */
function toPlanId(tier?: string): PlanId {
  if (tier === 'STUDIO') return 'premium';
  if (tier === 'PRO' || tier === 'BASIC') return 'pro';
  return 'free';
}

// Billing history is read from `GET /v1/purchases` (the same endpoint RefundsSection
// already uses). It used to be `const INVOICES = []` — a hardcoded empty array that
// nothing ever populated, dating from when there was no billing backend. Billing is
// live now, so that constant made the "No billing history yet" empty state
// unconditional: it stayed empty after any number of successful payments.
//
// Statuses that mean money actually moved. A purchase the user abandoned at checkout
// (CHECKOUT_CREATED) is not billing history; a payment that succeeded but whose
const BILLED_STATUSES = new Set([
  'PAYMENT_COMPLETED',
  'FULFILLMENT_IN_PROGRESS',
  'COMPLETED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
]);

// A purchase where money moved: either in a billed status or has a paid/downloadable invoice
// (e.g. cancelled-then-refunded purchase, PQ-03) or an overpayment credited to wallet (PQ-31).
function isBilledPurchase(p: PurchaseListItem): boolean {
  return BILLED_STATUSES.has(p.status) || Boolean(p.invoice?.downloadable) || Boolean(p.walletCredit);
}

function statusTone(status: string, hasDownloadableInvoice?: boolean, hasWalletCredit?: boolean): 'success' | 'warning' | 'neutral' {
  if (status === 'COMPLETED' || hasWalletCredit) return 'success';
  if (status === 'REFUNDED' || status === 'PARTIALLY_REFUNDED' || (status === 'CANCELLED' && hasDownloadableInvoice)) {
    return 'warning';
  }
  return 'neutral';
}

function formatStatus(status: string, hasDownloadableInvoice?: boolean, hasWalletCredit?: boolean): string {
  if (hasWalletCredit) {
    return 'credited to wallet';
  }
  if (status === 'CANCELLED' && hasDownloadableInvoice) {
    return 'refunded';
  }
  return status.replace(/_/g, ' ').toLowerCase();
}

export function SubscriptionSection() {
  const { user } = useAuth();
  const { toast } = useToast();
  const permissions = useAuthStore((s) => s.permissions);
  const canBuyCreditPacks = permissions.includes('PURCHASE_CREDIT_PACK');
  const { data: entitlementData } = usePublishEntitlement();
  const [invoices, setInvoices] = useState<PurchaseListItem[]>([]);
  const [pendingOrders, setPendingOrders] = useState<PurchaseListItem[]>([]);
  const [payingOrderId, setPayingOrderId] = useState<string | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handlePayPendingOrder = async (order: PurchaseListItem) => {
    if (payingOrderId) return;
    setPayingOrderId(order.id);
    try {
      const idempotencyKey =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `retry-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const detail =
        order.status === 'PAYMENT_PENDING'
          ? await retryCheckout(order.id, idempotencyKey)
          : await createCheckout(order.id);

      await openRazorpayCheckout({
        keyId: detail.checkout.keyId,
        customerId: detail.checkout.customerId,
        gatewayOrderId: detail.checkout.gatewayOrderId,
        amountPaise: detail.checkout.amountPaise,
        currency: detail.checkout.currency,
        onSuccess: async (response) => {
          void relayCheckoutResult(order.id, response);
          toast('Payment successful! Updating order status…', 'success');
          await pollPurchaseUntilResolved(order.id, () => true);
          const refreshed = await listPurchases();
          const now = Date.now();
          setInvoices(refreshed.items.filter(isBilledPurchase));
          setPendingOrders(
            refreshed.items.filter((p) => {
              if (p.status !== 'PAYMENT_PENDING') return false;
              const exp = (p as unknown as { expiresAt?: string }).expiresAt;
              if (exp && new Date(exp).getTime() <= now) return false;
              return true;
            }),
          );
          setPayingOrderId(null);
        },
        onDismiss: () => {
          setPayingOrderId(null);
        },
        onFailure: (err) => {
          toast(err.description || 'Payment failed', 'error');
          setPayingOrderId(null);
        },
      });
    } catch (err) {
      toast(err instanceof CheckoutUnavailableError ? PAYMENT_UNAVAILABLE_MESSAGE : err instanceof Error ? err.message : 'Could not start payment', 'error');
      setPayingOrderId(null);
    }
  };

  const handleDownload = async (purchaseId: string, invoiceNumber: string) => {
    if (downloadingId) return; // one at a time; also blocks double-click
    setDownloadingId(purchaseId);
    try {
      await downloadInvoice(purchaseId, invoiceNumber);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Couldn’t download the invoice. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const [liveUsage, setLiveUsage] = useState({
    invitations: 0,
    rsvps: 0,
  });

  useEffect(() => {
    let cancelled = false;
    listPurchases()
      .then((res) => {
        if (cancelled) return;
        const now = Date.now();
        setInvoices(res.items.filter(isBilledPurchase));
        setPendingOrders(
          res.items.filter((p) => {
            if (p.status !== 'PAYMENT_PENDING') return false;
            const exp = (p as unknown as { expiresAt?: string }).expiresAt;
            if (exp && new Date(exp).getTime() <= now) return false;
            return true;
          }),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        // Surfaced, not swallowed: a user who has paid must never be told
        // "no billing history yet" because a request failed.
        setInvoicesError(err instanceof Error ? err.message : 'Could not load your billing history');
      })
      .finally(() => !cancelled && setInvoicesLoading(false));

    Promise.all([
      getInvitationStats().catch(() => null),
      listInvitations({ limit: 100 }).catch(() => null),
    ]).then(([stats, list]) => {
      if (cancelled) return;
      const invitations = stats?.total ?? list?.items?.length ?? 0;
      const rsvps = list?.items?.reduce((sum, item) => sum + (item.totalRsvps || 0), 0) ?? 0;
      setLiveUsage({ invitations, rsvps });
    });

    return () => {
      cancelled = true;
    };
  }, []);
  const currentPlan = toPlanId(user?.plan);
  const remainingCredits = entitlementData?.remainingCredits ?? 0;

  const usageItems = [
    {
      label: 'Invitation websites',
      value: liveUsage.invitations,
      description: 'Total invitations created',
      icon: Icons.Templates,
    },
    {
      label: 'Available credits',
      value: remainingCredits,
      description: 'Non-expiring publish credits',
      icon: Icons.Zap,
    },
    {
      label: 'RSVP responses',
      value: liveUsage.rsvps,
      description: 'Total guest replies recorded',
      icon: Icons.Users,
    },
  ];

  // AC-B5: the old "Payment method" card collected card details into localStorage for
  // subscription renewals that never existed. It is gone; wipe any summary a browser
  // still holds from it.
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_PAYMENT_METHOD_KEY);
    } catch {
      /* storage unavailable — nothing to clear */
    }
  }, []);

  return (
    <div className="flex flex-col gap-6">
      {/* Current plan */}
      <SettingsCard className="overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <Icons.Crown size={22} />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-on-surface">
                  {PLAN_LABELS[currentPlan]} plan
                </h3>
                <Pill tone="primary">Current</Pill>
              </div>
              <p className="text-sm text-on-surface-variant">
                {canBuyCreditPacks
                  ? 'Partner tier · Wholesale credit pack publishing'
                  : 'Standard tier · Pay-per-publish or wallet credits'}
              </p>
            </div>
          </div>
          <Pill tone="primary">Volume Credit Packs</Pill>
        </div>
      </SettingsCard>

      {/* Partner Credit Packs */}
      {/*
        Partner-only. Gated on PURCHASE_CREDIT_PACK, rather than on a role string, so
        the UI cannot offer something the API would refuse. An individual account has no
        UserRole rows and therefore no commerce permissions, so it sees nothing here.

        NOTE: when first written this comment claimed the backend "enforces" that
        permission. It did not — `PURCHASE_CREDIT_PACK` was defined in rbac.ts and read
        by nothing, so any authenticated account could buy a partner pack. Caught in
        review. Enforcement now exists in `PurchaseApplicationService.createPurchase`,
        keyed on the RESOLVED product kind (not on which DTO field carried the code,
        which was bypassable), so this UI gate is once again only a convenience and the
        server is the authority.
      */}
      {canBuyCreditPacks && <PartnerCreditPacksSection />}

      {/* Usage */}
      <SettingsCard
        icon={Icons.Analytics}
        title="Current usage"
        description="Your live account creation and publishing metrics."
      >
        <div className="grid sm:grid-cols-3 gap-5">
          {usageItems.map((u) => (
            <div key={u.label} className="rounded-2xl bg-surface-low p-5 border border-outline-variant/20">
              <div className="flex items-center gap-2 text-on-surface-variant">
                <u.icon size={16} />
                <span className="text-xs font-semibold">{u.label}</span>
              </div>
              <p className="mt-3 text-2xl font-bold text-on-surface">
                {u.value}
              </p>
              <p className="mt-1 text-xs text-on-surface-variant">
                {u.description}
              </p>
            </div>
          ))}
        </div>
      </SettingsCard>

      {/* Unpaid orders */}
      {pendingOrders.length > 0 && (
        <SettingsCard
          icon={Icons.Subscription}
          title="Unpaid orders"
          description="Orders waiting for payment. Complete payment to activate your purchase."
        >
          <div className="divide-y divide-outline-variant/15">
            {pendingOrders.map((order) => (
              <div key={order.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-on-surface truncate">{order.orderNumber}</p>
                  <p className="text-xs text-on-surface-variant">
                    {new Date(order.createdAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                    {order.collectionCode ? ` · ${order.collectionCode}` : ''}
                  </p>
                </div>
                <span className="text-sm font-semibold text-on-surface">
                  {formatPrice(order.amount.totalPaise, order.amount.currency, { exact: true })}
                </span>
                <Pill tone="warning">Payment pending</Pill>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={payingOrderId === order.id}
                  onClick={() => void handlePayPendingOrder(order)}
                >
                  {payingOrderId === order.id ? 'Opening…' : 'Pay now'}
                </Button>
              </div>
            ))}
          </div>
        </SettingsCard>
      )}

      {/* Billing history. Download appears only when the backend says the invoice is PAID. */}
      <SettingsCard
        icon={Icons.History}
        title="Billing history"
        description="Your payments and invoices on InviteCloud."
      >
        {invoicesLoading ? (
          <div className="flex items-center gap-3 py-4 text-sm text-on-surface-variant">
            <Icons.Loader size={16} className="animate-spin" />
            <span>Loading your billing history…</span>
          </div>
        ) : invoicesError ? (
          <div className="flex items-center gap-4 rounded-2xl border border-dashed border-error/40 bg-error/5 p-4">
            <div className="flex-1">
              <p className="text-sm font-semibold text-on-surface">Couldn’t load billing history</p>
              <p className="text-xs text-on-surface-variant">{invoicesError}</p>
            </div>
          </div>
        ) : invoices.length > 0 ? (
          <div className="divide-y divide-outline-variant/15">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-on-surface truncate">{inv.orderNumber}</p>
                  <p className="text-xs text-on-surface-variant">
                    {new Date(inv.createdAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                    {inv.collectionCode ? ` · ${inv.collectionCode}` : ''}
                    {inv.hasRefundedPayment && inv.status === 'COMPLETED' ? ' · Duplicate payment refunded' : ''}
                    {inv.walletCredit ? ` · Paid after expiry — ₹${(inv.walletCredit.amountPaise / 100).toLocaleString('en-IN')} added to your wallet` : ''}
                  </p>
                </div>
                <span className="text-sm font-semibold text-on-surface">
                  {formatPrice(inv.amount.totalPaise, inv.amount.currency, { exact: true })}
                </span>
                <Pill tone={statusTone(inv.status, Boolean(inv.invoice?.downloadable), Boolean(inv.walletCredit))}>
                  {formatStatus(inv.status, Boolean(inv.invoice?.downloadable), Boolean(inv.walletCredit))}
                </Pill>
                {inv.invoice?.downloadable && (
                  <button
                    type="button"
                    onClick={() => handleDownload(inv.id, inv.invoice!.number)}
                    disabled={downloadingId !== null}
                    aria-label={`Download invoice ${inv.invoice.number}`}
                    className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-on-surface-variant hover:bg-surface-low hover:text-on-surface disabled:opacity-50"
                  >
                    {downloadingId === inv.id ? (
                      <Icons.Loader size={16} className="animate-spin" />
                    ) : (
                      <Icons.Download size={16} />
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-4 rounded-2xl border border-dashed border-outline-variant/50 bg-surface-low/50 p-4">
            <div className="w-12 h-8 rounded-md bg-surface-lowest border border-outline-variant/40 flex items-center justify-center text-on-surface-variant flex-shrink-0">
              <Icons.History size={16} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-on-surface">No billing history yet</p>
              <p className="text-xs text-on-surface-variant">
                Invoices will appear here after your first payment.
              </p>
            </div>
          </div>
        )}
      </SettingsCard>

    </div>
  );
}
